import { useMemo } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { ActionDropdown, getActions, type ActionContext, type CreateTarget } from 'actions';
import { type NodeType } from 'redux/slices/lists/nodeTypesSlice';
import { type WorkspaceItem } from 'api/workspace/endpoints/getWorkspaceContents';
import { toActionEntity } from './toActionEntity';

/**
 * The per-row actions dropdown for a contents row. Standalone so it can memoize
 * the projected entity and the resolved actions (recomputing only when the row,
 * node-types LOV, or context change — `createIn` is a stable computed from the
 * model). Shows only `'instance'`-scoped actions (the create/"type" actions,
 * always present, are for the toolbar).
 */
export const WorkspaceRowActions = ({
  item,
  knownNodeTypes,
  refresh,
  navigate,
  createIn,
}: {
  readonly item: WorkspaceItem;
  readonly knownNodeTypes: NodeType[];
  readonly refresh: () => void;
  readonly navigate: NavigateFunction;
  readonly createIn?: CreateTarget;
}) => {
  const entity = useMemo(() => toActionEntity(item, knownNodeTypes), [item, knownNodeTypes]);
  const { actions, items } = useMemo(() => {
    if (entity === null) return { actions: {}, items: [] as string[] };
    const resolved = getActions({ entities: [entity], refresh, navigate, createIn } satisfies ActionContext);
    const ids = Object.values(resolved)
      .filter((action) => action.scope === 'instance')
      .map((action) => action.id);
    return { actions: resolved, items: ids };
  }, [entity, refresh, navigate, createIn]);

  if (entity === null) return null;
  return <ActionDropdown actions={actions} items={items} />;
};
